import type { PortalAssetTitle } from '../../types/portalAsset.js';
import { assetNameSource } from './displayName.js';
import { MAX_ASSET_TAGS, MAX_ASSET_TAG_LENGTH } from './tags.js';

// THE TWO DERIVED INDEX FIELDS (`sortName`, `searchTokens`) AND THE TITLE RULES — PURE.
// ⚠️ APP-ONLY: the asset worker writes contentType / dimensions / rendition paths and touches none of the
// three inputs, so it must never import this (checked with `npm run deploy:check`'s skew list).
//
// ⚠️⚠️ EVERY WRITE PATH CALLS `deriveAssetIndexFields` AND NOTHING ELSE COMPUTES THESE FIELDS
// (derivedWritePaths.test.ts). Firestore's `orderBy` returns only documents that HAVE the ordered field,
// so an asset missing `sortName` does not error when someone sorts by Name — it DISAPPEARS from the grid;
// `array-contains` behaves the same for `searchTokens`. There is no backfill (the collection was cleared
// first); the guards are that test, `npm run report:portal-asset-missing-derived`, and the pinned
// semantics in derived.test.ts.

/** ⚠️ Chosen near the tag cap's order of magnitude; the token-count proof below depends on it. */
export const MAX_ASSET_TITLE_LENGTH = 100;
/** The filename cap the upload schema enforces (schemas.ts imports THIS, so the proof and the schema cannot drift). */
export const MAX_ASSET_FILENAME_LENGTH = 400;

/**
 * Prefixes are emitted from length 2 up to this ceiling per word. ⚠️ A query word LONGER than the ceiling can
 * only match on its first PREFIX_MAX characters, so the query path must filter the returned rows exactly in
 * memory (`matchesSearchQuery`) — without that, search returns confident wrong rows.
 */
export const SEARCH_PREFIX_MIN = 2;
export const SEARCH_PREFIX_MAX = 8;

/**
 * `sortName` is a SORT KEY, not content, so it is clamped: Firestore truncates indexed values past 1,500
 * bytes and "queries involving truncated field values may return inconsistent results" (quotas page). 200
 * CODE POINTS is at most 800 UTF-8 bytes (4 per code point).
 * ⚠️ Clamped by CODE POINT, never by UTF-16 unit: `.slice` can cut through a surrogate pair and leave a lone surrogate,
 * which Firestore REFUSES to store (2 UNKNOWN) — inside createUploads' transaction, failing a whole batch.
 */
export const MAX_SORT_NAME_LENGTH = 200;

export function clampSortName(s: string): string {
  const chars = Array.from(s); // iterates by code point
  return chars.length <= MAX_SORT_NAME_LENGTH ? s : chars.slice(0, MAX_SORT_NAME_LENGTH).join('');
}

export type TitleNormalization = { ok: true; text: string | null } | { ok: false; reason: string };

/**
 * trim, collapse internal whitespace; empty or whitespace-only = null (cleared, never a title of spaces).
 * ⚠️ Over the cap is REFUSED, never truncated (the tags precedent): silently cutting a club's title is
 * absence read as a value.
 */
export function normalizeAssetTitle(raw: string | null): TitleNormalization {
  if (raw === null) return { ok: true, text: null };
  const text = raw.trim().replace(/\s+/g, ' ');
  if (text === '') return { ok: true, text: null };
  if (text.length > MAX_ASSET_TITLE_LENGTH) {
    return { ok: false, reason: `A title can be at most ${MAX_ASSET_TITLE_LENGTH} characters.` };
  }
  return { ok: true, text };
}

/** `DJI_0496.JPG` -> `DJI_0496`. Only a short alphanumeric suffix counts as an extension. */
export function stripExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return filename;
  return /^[A-Za-z0-9]{1,8}$/.test(filename.slice(dot + 1)) ? filename.slice(0, dot) : filename;
}

/** Lowercase, fold accents (`café` -> `cafe`), split on anything outside [a-z0-9]. */
export function wordsOf(text: string): string[] {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w !== '');
}

type IndexInput = {
  title: PortalAssetTitle | null;
  originalFilename: string;
  tags: readonly string[];
};

function assetWords(input: IndexInput): string[] {
  return [
    ...(input.title ? wordsOf(input.title.text) : []),
    // ⚠️ The extension is stripped: tokenising `jpg` would make a search for "jpg" return every photo.
    ...wordsOf(stripExtension(input.originalFilename)),
    ...input.tags.flatMap(wordsOf),
  ];
}

export function deriveAssetIndexFields(input: IndexInput): { sortName: string; searchTokens: string[] } {
  // ⚠️ The TITLE when there is one, because the grid shows the title: sorting a control labelled "Name" by a
  // string the viewer cannot see is the house failure class. Lowercased because Firestore sorts by byte order
  // (otherwise `IMG_10` precedes `img_2` and every capital precedes every lower-case letter).
  // ⚠️ The SAME source string the grid displays (assetNameSource), lowercased and clamped: one is the name, the other its sort key.
  const sortName = assetNameSource({ title: input.title?.text, originalFilename: input.originalFilename }).toLowerCase();
  const tokens = new Set<string>();
  for (const word of assetWords(input)) {
    for (let n = SEARCH_PREFIX_MIN; n <= Math.min(word.length, SEARCH_PREFIX_MAX); n++) tokens.add(word.slice(0, n));
  }
  return { sortName: clampSortName(sortName), searchTokens: [...tokens].sort() };
}

/**
 * The ONE token a query hands to `array-contains`: the longest word's first SEARCH_PREFIX_MAX characters
 * (the most selective). null = nothing searchable (every word under SEARCH_PREFIX_MIN characters).
 */
export function searchQueryToken(query: string): string | null {
  const words = wordsOf(query).filter((w) => w.length >= SEARCH_PREFIX_MIN);
  if (words.length === 0) return null;
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a));
  return longest.slice(0, SEARCH_PREFIX_MAX);
}

/**
 * The EXACT in-memory filter run after the `array-contains`: every query word must be a prefix of one of the
 * asset's words. ⚠️ Required, not optional — two long words sharing their first SEARCH_PREFIX_MAX characters
 * are indistinguishable to the index.
 */
export function matchesSearchQuery(input: IndexInput, query: string): boolean {
  const q = wordsOf(query);
  if (q.length === 0) return false;
  const words = assetWords(input);
  return q.every((qw) => words.some((w) => w.startsWith(qw)));
}

/**
 * PROVABLE UPPER BOUND on the distinct tokens one source of `chars` characters can yield. A word of length L
 * yields at most min(L, MAX) - 1 prefixes and costs L + 1 characters (itself plus a separator), and
 * (min(L,MAX) - 1) <= (MAX - 1)/(MAX + 1) * (L + 1) for every L, so the sum over words is bounded by
 * (MAX - 1)/(MAX + 1) * (chars + 1). Equality is reached by words of exactly MAX characters.
 */
export function maxTokensFromChars(chars: number): number {
  return Math.ceil(((SEARCH_PREFIX_MAX - 1) * (chars + 1)) / (SEARCH_PREFIX_MAX + 1));
}

/**
 * The most `searchTokens` any asset can carry, from the caps: the title, the filename (extension counted, an
 * over-estimate) and MAX_ASSET_TAGS tags of MAX_ASSET_TAG_LENGTH characters. ⚠️ The array is NEVER truncated —
 * dropping tokens makes search silently miss — so this number is the proof it needs no truncating.
 */
export function maxSearchTokens(): number {
  return (
    maxTokensFromChars(MAX_ASSET_TITLE_LENGTH) +
    maxTokensFromChars(MAX_ASSET_FILENAME_LENGTH) +
    MAX_ASSET_TAGS * maxTokensFromChars(MAX_ASSET_TAG_LENGTH)
  );
}
